import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalEmpty,
  PortalInput,
  PortalModal,
  PortalSelect,
  PortalStatus,
  PortalTextarea,
} from '../../components/portal/PortalElements';

const activeStatuses = new Set(['pending', 'confirmed']);

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    specialRequest: '',
    tablePreference: '',
    paymentStatus: 'pending',
    couponCode: '',
  });

  async function loadBookings() {
    setLoading(true);
    try {
      const response = await adminApi.get('/admin/bookings', {
        params: search ? { search } : undefined,
      });
      setBookings(response.data || []);
      setError('');
    } catch (requestError) {
      setError(requestError.message || 'Unable to load bookings.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadBookings();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search]);

  const filteredBookings = useMemo(
    () =>
      bookings.filter((booking) => {
        const matchesStatus =
          statusFilter === 'active'
            ? activeStatuses.has(booking.status)
            : statusFilter === 'all'
              ? true
              : booking.status === statusFilter;
        const matchesDate = dateFilter ? booking.date === dateFilter : true;
        return matchesStatus && matchesDate;
      }),
    [bookings, dateFilter, statusFilter],
  );

  async function runAction(bookingId, action) {
    try {
      await adminApi.post(`/admin/bookings/${bookingId}/${action}`);
      toast.success(`Booking ${action.replace('-', ' ')} success.`);
      await loadBookings();
      if (selectedBooking?.bookingId === bookingId) {
        setSelectedBooking(null);
      }
    } catch (requestError) {
      toast.error(requestError.message || `Unable to ${action} booking.`);
    }
  }

  async function saveEdits() {
    if (!selectedBooking) {
      return;
    }

    try {
      await adminApi.patch(`/admin/bookings/${selectedBooking.bookingId}`, {
        specialRequest: form.specialRequest,
        tablePreference: form.tablePreference,
        paymentStatus: form.paymentStatus,
        couponCode: form.couponCode,
      });
      toast.success('Booking updated.');
      setSelectedBooking(null);
      await loadBookings();
    } catch (requestError) {
      toast.error(requestError.message || 'Unable to update booking.');
    }
  }

  function openBooking(booking) {
    setSelectedBooking(booking);
    setForm({
      specialRequest: booking.specialRequest || '',
      tablePreference: booking.tablePreference || '',
      paymentStatus: booking.paymentStatus || 'pending',
      couponCode: booking.couponCode || '',
    });
  }

  return (
    <div className="portal-stack">
      <PortalCard>
        <div className="portal-filters">
          <PortalInput
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Booking ID, guest, phone, email"
          />
          <PortalSelect
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="checked_in">Checked in</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No show</option>
          </PortalSelect>
          <PortalInput
            label="Date"
            type="date"
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
          />
          <PortalButton variant="secondary" onClick={() => { setSearch(''); setStatusFilter('active'); setDateFilter(''); }}>
            Reset
          </PortalButton>
        </div>
      </PortalCard>

      {error ? <div className="portal-error">{error}</div> : null}

      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Bookings</p>
            <h2>Reservation queue</h2>
          </div>
          {loading ? <span className="portal-muted">Loading...</span> : null}
        </div>
        {filteredBookings.length ? (
          <div className="portal-table-wrap">
            <table className="portal-table admin-bookings-table">
              <thead>
                <tr>
                  <th>Booking</th>
                  <th>Guest</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Table Preference</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.bookingId}>
                    <td>{booking.bookingId}</td>
                    <td>{booking.customerName} ({booking.pax})</td>
                    <td>{booking.date}</td>
                    <td>{booking.time}</td>
                    <td>{booking.tablePreference || '-'}</td>
                    <td>
                      <div className="portal-action-row">
                        <PortalButton variant="secondary" onClick={() => openBooking(booking)}>
                          View
                        </PortalButton>
                        {['pending', 'confirmed'].includes(booking.status) ? (
                          <PortalButton onClick={() => runAction(booking.bookingId, 'check-in')}>
                            Check in
                          </PortalButton>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <PortalEmpty title="No bookings match" body="Try a different search or status filter." />
        )}
      </PortalCard>

      <PortalModal
        open={Boolean(selectedBooking)}
        title={selectedBooking ? `Booking ${selectedBooking.bookingId}` : 'Booking'}
        onClose={() => setSelectedBooking(null)}
      >
        {selectedBooking ? (
          <div className="portal-stack">
            <div className="portal-detail-grid">
              <div><strong>Guest</strong><span>{selectedBooking.customerName}</span></div>
              <div><strong>Email</strong><span>{selectedBooking.email || '-'}</span></div>
              <div><strong>Phone</strong><span>{selectedBooking.phone || '-'}</span></div>
              <div><strong>Schedule</strong><span>{selectedBooking.date} at {selectedBooking.time}</span></div>
              <div><strong>Guests</strong><span>{selectedBooking.pax}</span></div>
              <div><strong>Table preference</strong><span>{selectedBooking.tablePreference || '-'}</span></div>
              <div><strong>Total</strong><span>Rs. {selectedBooking.totalAmount || 0}</span></div>
            </div>
            <PortalInput
              label="Table preference"
              value={form.tablePreference}
              onChange={(event) => setForm((current) => ({ ...current, tablePreference: event.target.value }))}
              placeholder="Indoor / Outdoor / No Preference"
            />
            <PortalSelect
              label="Payment status"
              value={form.paymentStatus}
              onChange={(event) => setForm((current) => ({ ...current, paymentStatus: event.target.value }))}
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
            </PortalSelect>
            <PortalInput
              label="Coupon code"
              value={form.couponCode}
              onChange={(event) => setForm((current) => ({ ...current, couponCode: event.target.value }))}
              placeholder="Optional coupon"
            />
            <PortalTextarea
              label="Special request"
              rows="4"
              value={form.specialRequest}
              onChange={(event) => setForm((current) => ({ ...current, specialRequest: event.target.value }))}
              placeholder="Guest notes"
            />
            <div className="portal-action-row">
              <PortalButton onClick={saveEdits}>Save</PortalButton>
              {['pending', 'confirmed', 'checked_in'].includes(selectedBooking.status) ? (
                <PortalButton variant="secondary" onClick={() => runAction(selectedBooking.bookingId, 'cancel')}>
                  Cancel
                </PortalButton>
              ) : null}
              {selectedBooking.status === 'checked_in' ? (
                <PortalButton variant="secondary" onClick={() => runAction(selectedBooking.bookingId, 'complete')}>
                  Complete
                </PortalButton>
              ) : null}
              {['pending', 'confirmed'].includes(selectedBooking.status) ? (
                <PortalButton variant="secondary" onClick={() => runAction(selectedBooking.bookingId, 'no-show')}>
                  Mark no-show
                </PortalButton>
              ) : null}
            </div>
          </div>
        ) : null}
      </PortalModal>
    </div>
  );
}
